import { Injectable, Signal, inject, signal } from '@angular/core';
import { Client, IMessage, ReconnectionTimeMode, StompSubscription } from '@stomp/stompjs';
import { Observable, Subject } from 'rxjs';
import { LiveFeedConnection } from '../models/live-feed-connection.model';
import { RecordingFeedEvent } from '../models/object-capture.model';
import { StompAccessTokenService } from './stomp-access-token.service';
import { stompBrokerUrl, withBearerHeader } from './stomp-broker';

const RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * STOMP-Client fuer den Live-Kanal einer Aufnahme (NEX-1715, {@code RecordingFeedPublisher.topicFor}).
 * Aufbau wie {@link TestlaufLiveFeedService}: ein Dienst je Ansicht, Wiederaufbau im Client,
 * neues Abonnement nach jedem Reconnect.
 */
@Injectable()
export class RecordingFeedService {
  private readonly accessToken = inject(StompAccessTokenService);

  /** Ueberschreibbarer Seam fuer Tests — echte Verbindung nur im Betrieb. */
  clientFactory: (brokerUrl: string) => Client = (brokerUrl) =>
    new Client({
      brokerURL: brokerUrl,
      reconnectDelay: RECONNECT_DELAY_MS,
      maxReconnectDelay: MAX_RECONNECT_DELAY_MS,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
    });

  private readonly connectionState = signal<LiveFeedConnection>('connecting');
  readonly connection: Signal<LiveFeedConnection> = this.connectionState.asReadonly();

  private readonly eventSubject = new Subject<RecordingFeedEvent>();
  readonly events: Observable<RecordingFeedEvent> = this.eventSubject.asObservable();

  private client: Client | null = null;
  private subscription: StompSubscription | null = null;

  connect(projectId: string, mappingId: string): void {
    if (this.client) {
      return;
    }
    this.connectionState.set('connecting');
    const topic = `/topic/projects/${projectId}/object-mappings/${mappingId}/recording`;
    const client = this.clientFactory(stompBrokerUrl());
    withBearerHeader(client, () => this.accessToken.acquire());
    client.onConnect = () => {
      if (this.client !== client) {
        return;
      }
      this.connectionState.set('connected');
      this.subscription = client.subscribe(topic, (message: IMessage) => this.dispatch(message));
    };
    client.onWebSocketClose = () => {
      if (this.client === client) {
        this.subscription = null;
        this.connectionState.set('lost');
      }
    };
    client.onStompError = () => {
      if (this.client === client) {
        this.connectionState.set('lost');
      }
    };
    this.client = client;
    client.activate();
  }

  disconnect(): void {
    const client = this.client;
    this.client = null;
    this.subscription?.unsubscribe();
    this.subscription = null;
    void client?.deactivate();
  }

  private dispatch(message: IMessage): void {
    try {
      this.eventSubject.next(JSON.parse(message.body) as RecordingFeedEvent);
    } catch {
      return;
    }
  }
}
